import { useCallback, useMemo } from 'react';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';

export interface OutreachPlan {
  /** The plan pitches every day (`outreachPitchesPerDay > 0`): the trial too, Free not. */
  canPitch: boolean;
  canUseAutopilot: boolean;
  canExport: boolean;
  /** The read-only demo artist: writes are off (the server answers `M24_DEMO_READ_ONLY`). */
  isDemo: boolean;
  /** Runs `action` when the plan has `feature`; else opens the offer. The demo artist runs nothing. */
  guard: (feature: SMLUpgradeFeature, action: () => void, subject?: string) => void;
}

const FEATURE_ALLOWED: Partial<Record<SMLUpgradeFeature, keyof Omit<OutreachPlan, 'guard' | 'isDemo'>>> = {
  [SMLUpgradeFeature.outreach]: 'canPitch',
  [SMLUpgradeFeature.outreachAutopilot]: 'canUseAutopilot',
  [SMLUpgradeFeature.outreachExport]: 'canExport',
};

/**
 * The Outreach plan gates of the active workspace (Outreach v2: N new pitches a day). No reveal
 * and no pitch pack exist.
 */
export function useOutreachPlan(): OutreachPlan {
  const capabilities = useMusic24Capabilities();
  const isDemo = useIsDemoAccount();
  const openUpgradePrompt = useUpgradePrompt();

  const flags = useMemo(
    () => ({
      // Outreach v2: "may pitch" is a daily rate above 0 (controller ruling, Task 6).
      canPitch: capabilities.outreachPitchesPerDay > 0,
      canUseAutopilot: capabilities.canUseOutreachAutopilot,
      canExport: capabilities.canExportOutreachContacts,
    }),
    [capabilities]
  );

  const guard = useCallback(
    (feature: SMLUpgradeFeature, action: () => void, subject?: string) => {
      if (isDemo) {
        return;
      }
      const key = FEATURE_ALLOWED[feature];
      if (key === undefined || flags[key]) {
        action();
        return;
      }
      openUpgradePrompt(feature, subject);
    },
    [flags, isDemo, openUpgradePrompt]
  );

  return { ...flags, isDemo, guard };
}
